import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  FileText, Calendar, Users, Send, ChevronRight, CheckCircle,
  Clock, AlertTriangle, Eye, Bell, BarChart3, Settings,
  Search, ClipboardList, Building2, Briefcase, ArrowLeft
} from 'lucide-react';

import ReportFormView from '@/components/periodic/ReportFormView';
import CombinedReportView from '@/components/periodic/CombinedReportView';
import CycleDetailView from '@/components/periodic/CycleDetailView';
import ReportRequestWizard from '@/components/periodic/ReportRequestWizard';
import ReportSettings from '@/components/periodic/ReportSettings';

const STATUS_COLORS = {
  requested: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-cyan-100 text-cyan-700',
  returned: 'bg-red-100 text-red-700',
  resubmitted: 'bg-purple-100 text-purple-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  locked: 'bg-slate-800 text-white',
  overdue: 'bg-red-200 text-red-800',
  active: 'bg-blue-100 text-blue-700',
  all_submitted: 'bg-emerald-100 text-emerald-700',
  generated: 'bg-cyan-100 text-cyan-700',
};

export default function PeriodicReports() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [view, setView] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [register, setRegister] = useState([]);
  const [settings, setSettings] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeReport, setActiveReport] = useState(null);
  const [activeCycle, setActiveCycle] = useState(null);
  const [combinedReport, setCombinedReport] = useState(null);

  const [calendarEvents, setCalendarEvents] = useState([]);
  const [comparison, setComparison] = useState(null);

  const [requestStep, setRequestStep] = useState(0);
  const [requestData, setRequestData] = useState({
    period_start: '', period_end: '', due_date: '',
    participant_ids: [], participant_type_filter: 'all',
    instruction_message: ''
  });
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());

  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCompany, setFilterCompany] = useState('');

  const isAdmin = ['admin', 'director', 'project_director', 'project_manager',
    'contracts_manager', 'commercial_manager', 'site_agent', 'site_manager',
    'construction_manager', 'contracts_director'].includes(user?.role);

  // ─── DATA LOADING ───
  const loadStats = useCallback(async () => {
    try { const r = await api.get(`/periodic-reports/dashboard/${projectId}`); setStats(r.data); } catch (e) { console.error(e); }
  }, [projectId]);

  const loadCycles = useCallback(async () => {
    try { const r = await api.get(`/periodic-reports/cycles/${projectId}`); setCycles(r.data.cycles || []); } catch (e) { console.error(e); }
  }, [projectId]);

  const loadRegister = useCallback(async () => {
    try {
      let url = `/periodic-reports/register/${projectId}?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterType) url += `participant_type=${filterType}&`;
      if (filterCompany) url += `company=${filterCompany}&`;
      const r = await api.get(url);
      setRegister(r.data.register || []);
    } catch (e) { console.error(e); }
  }, [projectId, filterStatus, filterType, filterCompany]);

  const loadSettings = useCallback(async () => {
    try { const r = await api.get(`/periodic-reports/settings/${projectId}`); setSettings(r.data.settings || {}); setParticipants(r.data.participants || []); } catch (e) { console.error(e); }
  }, [projectId]);

  const loadTeamMembers = useCallback(async () => {
    try { const r = await api.get('/users'); setTeamMembers(r.data.users || r.data || []); } catch (e) { console.error(e); }
  }, []);

  const loadCalendar = useCallback(async () => {
    try { const r = await api.get(`/periodic-reports/calendar/${projectId}`); setCalendarEvents(r.data.events || []); } catch (e) { console.error(e); }
  }, [projectId]);

  const loadComparison = useCallback(async () => {
    try { const r = await api.get(`/periodic-reports/comparison/${projectId}`); setComparison(r.data.comparison); } catch (e) { console.error(e); }
  }, [projectId]);

  useEffect(() => {
    if (projectId) { loadStats(); loadCycles(); loadSettings(); }
  }, [projectId, loadStats, loadCycles, loadSettings]);

  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) openReport(reportId);
  }, [searchParams]);

  // ─── ACTIONS ───
  const openReport = async (reportId) => {
    setLoading(true);
    try { const r = await api.get(`/periodic-reports/report/${reportId}`); setActiveReport(r.data); setView('report'); }
    catch (e) { toast.error('Failed to load report'); }
    setLoading(false);
  };

  const openCycle = async (cycleId) => {
    setLoading(true);
    try { const r = await api.get(`/periodic-reports/cycles/${projectId}/${cycleId}`); setActiveCycle(r.data); setView('cycle'); }
    catch (e) { toast.error('Failed to load cycle'); }
    setLoading(false);
  };

  const saveSettings = async () => {
    try { await api.put(`/periodic-reports/settings/${projectId}`, { settings, participants }); toast.success('Reporting settings saved'); }
    catch (e) { toast.error('Failed to save settings'); }
  };

  const openRequestWizard = () => {
    loadTeamMembers();
    loadSettings();
    setRequestStep(0);
    setSelectedParticipants(new Set());
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    const dueDate = new Date(today.getFullYear(), today.getMonth() + 1, 5).toISOString().split('T')[0];
    setRequestData({ period_start: monthStart, period_end: monthEnd, due_date: dueDate, participant_ids: [], participant_type_filter: 'all', instruction_message: '' });
    setView('request');
  };

  const sendReportRequests = async () => {
    setLoading(true);
    try {
      const ids = Array.from(selectedParticipants);
      await api.post(`/periodic-reports/cycles/${projectId}`, { ...requestData, participant_ids: ids.length > 0 ? ids : undefined });
      toast.success('Report requests sent successfully');
      setView('dashboard'); loadStats(); loadCycles();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send requests'); }
    setLoading(false);
  };

  const sendReminders = async (cycleId) => {
    try { const r = await api.post(`/periodic-reports/reminders/${cycleId}`); toast.success(`${r.data.reminders_sent} reminders sent`); }
    catch (e) { toast.error('Failed to send reminders'); }
  };

  const generateCombined = async (cycleId) => {
    setLoading(true);
    try { const r = await api.post(`/periodic-reports/combined/${cycleId}`); setCombinedReport(r.data); setView('combined'); toast.success('Combined report generated'); }
    catch (e) { toast.error('Failed to generate combined report'); }
    setLoading(false);
  };

  const openCombined = async (cycleId) => {
    setLoading(true);
    try { const r = await api.get(`/periodic-reports/combined/${cycleId}`); setCombinedReport(r.data); setView('combined'); }
    catch (e) { toast.error('Combined report not generated yet'); }
    setLoading(false);
  };

  const runAIAnalysis = async (reportId) => {
    setLoading(true);
    try { const r = await api.post(`/periodic-reports/ai/analyze-report/${reportId}`); if (r.data.success) { toast.success('AI analysis complete'); openReport(reportId); } else toast.error(r.data.error || 'AI analysis failed'); }
    catch (e) { toast.error('AI analysis failed'); }
    setLoading(false);
  };

  const runCycleAI = async (cycleId) => {
    setLoading(true);
    try { const r = await api.post(`/periodic-reports/ai/analyze-cycle/${cycleId}`); if (r.data.success) { toast.success('AI cycle analysis complete'); if (combinedReport) openCombined(cycleId); } else toast.error(r.data.error || 'Failed'); }
    catch (e) { toast.error('Failed'); }
    setLoading(false);
  };

  const escalateOverdue = async (cycleId) => {
    try { const r = await api.post(`/periodic-reports/escalate/${cycleId}`); toast.success(`Escalated ${r.data.escalated} overdue reports`); }
    catch (e) { toast.error('Escalation failed'); }
  };

  const reviewReport = async (reportId, action) => {
    try {
      if (action === 'accept') { await api.post(`/periodic-reports/report/${reportId}/accept`); toast.success('Report accepted'); }
      else if (action === 'lock') { await api.post(`/periodic-reports/report/${reportId}/lock`); toast.success('Report locked'); }
      else if (action === 'return') { const reason = prompt('Reason for returning the report:'); if (!reason) return; await api.post(`/periodic-reports/report/${reportId}/return`, { reason }); toast.success('Report returned for revision'); }
      if (activeCycle) openCycle(activeCycle.id);
    } catch (e) { toast.error('Action failed'); }
  };

  const addParticipant = (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    if (!member) return;
    if (participants.find(p => p.user_id === userId)) { toast.error('Already added'); return; }
    setParticipants([...participants, {
      user_id: member.id,
      user_name: `${member.name || member.first_name || ''} ${member.surname || member.last_name || ''}`.trim(),
      company_name: member.company_name || '',
      email: member.email || '',
      role: member.role || '',
      discipline: member.discipline || '',
      participant_type: ['subcontractor'].includes(member.role) ? 'subcontractor' : 'consultant',
      mandatory: true,
      template_type: 'standard'
    }]);
  };

  const removeParticipant = (userId) => setParticipants(participants.filter(p => p.user_id !== userId));

  // ─── VIEW ROUTING ───
  if (view === 'report' && activeReport) {
    return <ReportFormView report={activeReport} setReport={setActiveReport} onBack={() => { setView('dashboard'); loadStats(); loadCycles(); }} isAdmin={isAdmin} onAI={runAIAnalysis} onReview={reviewReport} projectId={projectId} />;
  }

  if (view === 'combined' && combinedReport) {
    return <CombinedReportView combinedReport={combinedReport} onBack={() => setView('dashboard')} onRunCycleAI={runCycleAI} loading={loading} />;
  }

  if (view === 'request') {
    return <ReportRequestWizard onBack={() => setView('dashboard')} requestStep={requestStep} setRequestStep={setRequestStep} requestData={requestData} setRequestData={setRequestData} selectedParticipants={selectedParticipants} setSelectedParticipants={setSelectedParticipants} participants={participants} teamMembers={teamMembers} onSend={sendReportRequests} loading={loading} />;
  }

  if (view === 'settings') {
    return <ReportSettings onBack={() => setView('dashboard')} settings={settings} setSettings={setSettings} participants={participants} setParticipants={setParticipants} teamMembers={teamMembers} onSave={saveSettings} onAddParticipant={addParticipant} onRemoveParticipant={removeParticipant} />;
  }

  if (view === 'cycle' && activeCycle) {
    return <CycleDetailView activeCycle={activeCycle} onBack={() => setView('dashboard')} isAdmin={isAdmin} onSendReminders={sendReminders} onGenerateCombined={generateCombined} onOpenCombined={openCombined} onOpenReport={openReport} onReviewReport={reviewReport} onRunCycleAI={runCycleAI} loading={loading} />;
  }

  // ─── REGISTER VIEW ───
  if (view === 'register') {
    return (
      <DashboardLayout>
        <div className="max-w-6xl mx-auto space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => setView('dashboard')}><ArrowLeft className="h-4 w-4" /></Button>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900">Report Register</h1>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="requested">Requested</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="accepted">Accepted</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                  <SelectItem value="locked">Locked</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={v => setFilterType(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadRegister}><Search className="h-3.5 w-3.5 mr-1" /> Filter</Button>
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[700px]">
                <thead className="bg-slate-50 border-b"><tr>
                  <th className="text-left px-3 py-2.5">Period</th>
                  <th className="text-left px-3 py-2.5">Company</th>
                  <th className="text-left px-3 py-2.5">Discipline</th>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Due</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Submitted</th>
                  <th className="text-left px-3 py-2.5">Variance</th>
                  <th className="text-left px-3 py-2.5">Files</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr></thead>
                <tbody className="divide-y">{register.map(r => (
                  <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openReport(r.id)}>
                    <td className="px-3 py-2">{r.cycle_period}</td>
                    <td className="px-3 py-2 font-medium">{r.company_name}</td>
                    <td className="px-3 py-2">{r.discipline}</td>
                    <td className="px-3 py-2"><Badge className={`text-[10px] ${r.participant_type === 'consultant' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{r.participant_type}</Badge></td>
                    <td className="px-3 py-2">{r.due_date}</td>
                    <td className="px-3 py-2"><Badge className={`text-[10px] ${STATUS_COLORS[r.status] || ''}`}>{r.status?.replace(/_/g, ' ')}</Badge></td>
                    <td className="px-3 py-2">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}</td>
                    <td className="px-3 py-2">{r.days_variance !== null ? (r.days_variance > 0 ? <span className="text-red-600">{r.days_variance}d late</span> : <span className="text-emerald-600">{Math.abs(r.days_variance)}d early</span>) : '-'}</td>
                    <td className="px-3 py-2">{r.attachments_count}</td>
                    <td className="px-3 py-2 text-right"><ChevronRight className="h-3.5 w-3.5 text-slate-400 inline" /></td>
                  </tr>
                ))}</tbody>
              </table>
              </div>
              {register.length === 0 && <p className="text-center py-8 text-sm text-slate-400">No report records found</p>}
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // ─── CALENDAR VIEW ───
  if (view === 'calendar') {
    const grouped = {};
    calendarEvents.forEach(ev => {
      if (ev.date) { const month = ev.date.substring(0, 7); if (!grouped[month]) grouped[month] = []; grouped[month].push(ev); }
    });
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setView('dashboard')}><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Calendar className="h-5 w-5 text-cyan-600" /> Reporting Calendar</h1>
          </div>
          {Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a)).map(([month, evts]) => (
            <Card key={month}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">{new Date(month + '-01').toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {evts.sort((a, b) => a.date.localeCompare(b.date)).map(ev => (
                    <div key={ev.id} className="flex items-center gap-3 text-sm py-1 border-l-4 pl-3 rounded" style={{ borderColor: ev.color }}>
                      <span className="text-xs font-mono text-slate-500 w-20">{ev.date}</span>
                      <span className="font-medium text-slate-800 flex-1">{ev.title}</span>
                      <Badge className={`text-[10px] ${ev.type === 'due_date' ? 'bg-red-100 text-red-700' : ev.type === 'submission' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{ev.type?.replace(/_/g, ' ')}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          {calendarEvents.length === 0 && <p className="text-center py-12 text-sm text-slate-400">No reporting events yet.</p>}
        </div>
      </DashboardLayout>
    );
  }

  // ─── COMPARISON VIEW ───
  if (view === 'comparison' && comparison) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setView('dashboard')}><ArrowLeft className="h-4 w-4" /></Button>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-cyan-600" /> Period Comparison</h1>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="border-cyan-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-700">Current Period</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-2">{comparison.current_cycle?.period}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] text-slate-500">Submissions</p><p className="text-lg font-bold">{comparison.current_cycle?.submissions}</p></div>
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] text-slate-500">Issues</p><p className="text-lg font-bold">{comparison.current_cycle?.issues_count}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-500">Previous Period</CardTitle></CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-2">{comparison.previous_cycle?.period}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] text-slate-500">Submissions</p><p className="text-lg font-bold">{comparison.previous_cycle?.submissions}</p></div>
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-[10px] text-slate-500">Issues</p><p className="text-lg font-bold">{comparison.previous_cycle?.issues_count}</p></div>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-slate-500 mb-1">Submission Trend</p><p className={`text-sm font-bold ${comparison.submission_trend === 'improved' ? 'text-emerald-600' : comparison.submission_trend === 'declined' ? 'text-red-600' : 'text-slate-600'}`}>{comparison.submission_trend === 'improved' ? '↑ Improved' : comparison.submission_trend === 'declined' ? '↓ Declined' : '→ Stable'}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-slate-500 mb-1">Issues Trend</p><p className={`text-sm font-bold ${comparison.issues_trend === 'decreased' ? 'text-emerald-600' : comparison.issues_trend === 'increased' ? 'text-red-600' : 'text-slate-600'}`}>{comparison.issues_trend === 'decreased' ? '↓ Decreased' : comparison.issues_trend === 'increased' ? '↑ Increased' : '→ Stable'}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-[10px] text-slate-500 mb-1">New Issues</p><p className="text-sm font-bold text-slate-800">{comparison.new_issues_count}</p></CardContent></Card>
          </div>
          {comparison.current_issues?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Current Period Issues</CardTitle></CardHeader>
              <CardContent><div className="space-y-1.5">{comparison.current_issues.map((issue, i) => (
                <div key={i} className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <div><p className="text-xs font-medium text-slate-800">{issue.title}</p><p className="text-[10px] text-slate-400">{issue.company} — {issue.category}</p></div>
                  <Badge className={`text-[10px] ${issue.urgency === 'high' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{issue.urgency}</Badge>
                </div>
              ))}</div></CardContent>
            </Card>
          )}
          {comparison.repeat_missers?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-red-700">Repeat Non-Submitters</CardTitle></CardHeader>
              <CardContent><div className="flex flex-wrap gap-2">{comparison.repeat_missers.map(c => <Badge key={c} className="bg-red-100 text-red-700 text-xs">{c}</Badge>)}</div></CardContent>
            </Card>
          )}
        </div>
      </DashboardLayout>
    );
  }

  // ─── DASHBOARD VIEW ───
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="h-5 sm:h-6 w-5 sm:w-6 text-cyan-600" /> Periodic Reports</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">Consultant & Subcontractor Reporting</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { setView('register'); loadRegister(); }} data-testid="view-register-btn"><FileText className="h-4 w-4 mr-1" /> Register</Button>
            <Button variant="outline" size="sm" onClick={() => { loadCalendar(); setView('calendar'); }} data-testid="view-calendar-btn"><Calendar className="h-4 w-4 mr-1" /> Calendar</Button>
            <Button variant="outline" size="sm" onClick={() => { loadComparison(); setView('comparison'); }} data-testid="view-comparison-btn"><BarChart3 className="h-4 w-4 mr-1" /> Compare</Button>
            <Button variant="outline" size="sm" onClick={() => { loadTeamMembers(); setView('settings'); }} data-testid="reporting-settings-btn"><Settings className="h-4 w-4 mr-1" /> Settings</Button>
            {isAdmin && <Button onClick={openRequestWizard} className="bg-cyan-600 hover:bg-cyan-700" size="sm" data-testid="request-reports-btn"><Send className="h-4 w-4 mr-1" /> Request Reports</Button>}
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-3">
            {[
              { label: 'Total Cycles', value: stats.total_cycles, icon: Calendar, color: 'text-cyan-600' },
              { label: 'Reports Due', value: stats.reports_due, icon: FileText, color: 'text-blue-600' },
              { label: 'Submitted', value: stats.reports_submitted, icon: CheckCircle, color: 'text-emerald-600' },
              { label: 'Outstanding', value: stats.reports_outstanding, icon: Clock, color: 'text-amber-600' },
              { label: 'Overdue', value: stats.reports_overdue, icon: AlertTriangle, color: 'text-red-600' },
            ].map((s, i) => (
              <Card key={i}><CardContent className="p-2 sm:p-3 flex items-center gap-2"><s.icon className={`h-5 sm:h-6 w-5 sm:w-6 ${s.color}`} /><div><p className="text-[10px] text-slate-500 truncate">{s.label}</p><p className="text-base sm:text-lg font-bold">{s.value}</p></div></CardContent></Card>
            ))}
          </div>
        )}

        {stats?.active_cycle && (
          <Card className="border-cyan-200 bg-cyan-50/30" data-testid="active-cycle-card">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-cyan-600" /> Active Reporting Cycle</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  {isAdmin && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => sendReminders(stats.active_cycle.id)}><Bell className="h-3 w-3 mr-1" /> Remind</Button>}
                  {stats.reports_overdue > 0 && isAdmin && <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-200" onClick={() => escalateOverdue(stats.active_cycle.id)}><AlertTriangle className="h-3 w-3 mr-1" /> Escalate</Button>}
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openCycle(stats.active_cycle.id)}><Eye className="h-3 w-3 mr-1" /> View</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-sm">
                <div><span className="text-xs text-slate-500">Period:</span> <strong className="text-xs sm:text-sm">{stats.active_cycle.period_start} — {stats.active_cycle.period_end}</strong></div>
                <div><span className="text-xs text-slate-500">Due:</span> <strong className="text-xs sm:text-sm">{stats.active_cycle.due_date}</strong></div>
                <div className="flex items-center gap-2">
                  <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${stats.active_cycle.participant_count > 0 ? (stats.active_cycle.submitted_count / stats.active_cycle.participant_count) * 100 : 0}%` }} />
                  </div>
                  <span className="text-xs text-slate-600">{stats.active_cycle.submitted_count}/{stats.active_cycle.participant_count}</span>
                </div>
                {stats.combined_ready && isAdmin && (
                  <Button size="sm" className="h-7 text-xs bg-emerald-600" onClick={() => generateCombined(stats.active_cycle.id)}>
                    <FileText className="h-3 w-3 mr-1" /> Generate Combined
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <Button variant="outline" className="h-auto py-3 sm:py-4 flex flex-row sm:flex-col items-center gap-2" onClick={openRequestWizard} data-testid="request-consultants-btn">
              <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <span className="text-xs font-medium">Request Reports from Consultants</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 sm:py-4 flex flex-row sm:flex-col items-center gap-2" onClick={() => { setRequestData({...requestData, participant_type_filter: 'subcontractor'}); openRequestWizard(); }} data-testid="request-subcontractors-btn">
              <Briefcase className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <span className="text-xs font-medium">Request Reports from Subcontractors</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 sm:py-4 flex flex-row sm:flex-col items-center gap-2" onClick={openRequestWizard} data-testid="request-all-btn">
              <Users className="h-5 w-5 text-cyan-600 flex-shrink-0" />
              <span className="text-xs font-medium">Request Reports from All</span>
            </Button>
          </div>
        )}

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Reporting Cycles</CardTitle></CardHeader>
          <CardContent>
            {cycles.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-400">No reporting cycles yet. Click "Request Reports" to create one.</p>
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
              <table className="w-full text-xs min-w-[540px]">
                <thead className="bg-slate-50 border-b"><tr>
                  <th className="text-left px-3 py-2.5">Period</th>
                  <th className="text-left px-3 py-2.5">Due Date</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-left px-3 py-2.5">Progress</th>
                  <th className="text-left px-3 py-2.5">Combined</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr></thead>
                <tbody className="divide-y">{cycles.map(c => (
                  <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openCycle(c.id)}>
                    <td className="px-3 py-2.5 font-medium">{c.period_start} — {c.period_end}</td>
                    <td className="px-3 py-2.5">{c.due_date}</td>
                    <td className="px-3 py-2.5"><Badge className={`text-[10px] ${STATUS_COLORS[c.status] || ''}`}>{c.status?.replace(/_/g, ' ')}</Badge></td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${c.participant_count > 0 ? (c.submitted_count / c.participant_count) * 100 : 0}%` }} />
                        </div>
                        <span className="text-[10px]">{c.submitted_count}/{c.participant_count}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">{c.combined_report_id ? <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Ready</Badge> : <span className="text-slate-400">-</span>}</td>
                    <td className="px-3 py-2.5 text-right"><ChevronRight className="h-3.5 w-3.5 text-slate-400 inline" /></td>
                  </tr>
                ))}</tbody>
              </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
